/** @format */

import { useState } from "react";
import { useTranslation } from "react-i18next";

// --- Inline SVG Icons (no emoji, no external lib needed) ---
const EyeIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94' />
    <path d='M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19' />
    <line x1='1' y1='1' x2='23' y2='23' />
  </svg>
);

const LockIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
    <path d='M7 11V7a5 5 0 0 1 10 0v4' />
  </svg>
);

const MailIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
    <polyline points='22,6 12,13 2,6' />
  </svg>
);

const ShieldIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='28'
    height='28'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
    <polyline points='9 12 11 14 15 10' />
  </svg>
);

// --- Password Strength Logic ---
const getStrength = (password) => {
  if (!password) return { score: 0, label: "", color: "" };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const levels = [
    { label: "Weak", color: "#ef4444" },
    { label: "Fair", color: "#f97316" },
    { label: "Good", color: "#eab308" },
    { label: "Strong", color: "#22c55e" },
  ];
  return { score, ...(levels[score - 1] || { label: "", color: "" }) };
};

// --- Reusable Field ---
const PasswordField = ({ id, name, label, placeholder, value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={styles.fieldWrapper}>
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <div style={styles.inputRow}>
        <span style={styles.inputIcon}>
          <LockIcon />
        </span>
        <input
          id={id}
          name={name}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={styles.input}
          onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
          onBlur={(e) =>
            Object.assign(e.target.style, {
              borderColor: "#e2e8f0",
              boxShadow: "none",
            })
          }
          autoComplete='off'
        />
        <button
          type='button'
          onClick={() => setShow((s) => !s)}
          style={styles.eyeBtn}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
};

// --- Main Component ---
const ChangePassword = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: "",
    old_password: "",
    new_password: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const strength = getStrength(form.new_password);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2500);
  };

  return (
    <div style={styles.page} className='flex flex-row sm:flex-col'>
      {/* ─── Left: Form Card ─── */}
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.cardHeader}>
          <div style={styles.iconBadge}>
            <ShieldIcon />
          </div>
          <div>
            <h1 style={styles.title}>{t("Change Your Password")}</h1>
            <p style={styles.subtitle}>
              {t("Keep your account safe and secure")}
            </p>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Email */}
          <div style={styles.fieldWrapper}>
            <label htmlFor='email' style={styles.label}>
              Email
            </label>
            <div style={styles.inputRow}>
              <span style={styles.inputIcon}>
                <MailIcon />
              </span>
              <input
                id='email'
                name='email'
                type='email'
                placeholder='you@example.com'
                value={form.email}
                onChange={handleChange}
                style={styles.input}
                onFocus={(e) =>
                  Object.assign(e.target.style, styles.inputFocus)
                }
                onBlur={(e) =>
                  Object.assign(e.target.style, {
                    borderColor: "#e2e8f0",
                    boxShadow: "none",
                  })
                }
                autoComplete='email'
              />
            </div>
          </div>

          {/* Current Password */}
          <PasswordField
            id='o_password'
            name='old_password'
            label={t("Current Password")}
            placeholder={t("Enter current password")}
            value={form.old_password}
            onChange={handleChange}
          />

          {/* New Password */}
          <PasswordField
            id='n_password'
            name='new_password'
            label={t("New Password")}
            placeholder={t("Enter new password")}
            value={form.new_password}
            onChange={handleChange}
          />

          {/* Strength Meter */}
          {form.new_password && (
            <div style={styles.strengthWrapper}>
              <div style={styles.strengthBars}>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    style={{
                      ...styles.strengthBar,
                      backgroundColor:
                        strength.score >= i ? strength.color : "#e2e8f0",
                      transition: `background-color 0.3s ease ${i * 0.05}s`,
                    }}
                  />
                ))}
              </div>
              <span style={{ ...styles.strengthLabel, color: strength.color }}>
                {strength.label}
              </span>
            </div>
          )}

          {/* Submit */}
          <button
            type='submit'
            style={{
              ...styles.submitBtn,
              ...(submitted ? styles.submitBtnSuccess : {}),
            }}
            onMouseEnter={(e) =>
              !submitted && Object.assign(e.target.style, styles.submitBtnHover)
            }
            onMouseLeave={(e) =>
              !submitted &&
              Object.assign(e.target.style, {
                transform: "translateY(0)",
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
              })
            }
          >
            {submitted ? (
              <span style={styles.btnContent}>
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <polyline points='20 6 9 17 4 12' />
                </svg>
                {t("Password Updated!")}
              </span>
            ) : (
              <span style={styles.btnContent}>
                <LockIcon />
                {t("Update Password")}
              </span>
            )}
          </button>

          <p style={styles.helpText}>
            {t("Make sure your new password is at least 8 characters long.")}
          </p>
        </form>
      </div>

      {/* ─── Right: Illustration Panel ─── */}
      <div style={styles.illustrationPanel}>
        <div style={styles.illustrationInner}>
          <div style={styles.blob1} />
          <div style={styles.blob2} />
          <img
            src='/images/shopping.png'
            alt='security illustration'
            style={styles.illustration}
          />
          <div style={styles.illustrationCaption}>
            <span style={styles.captionDot} />
            <p style={styles.captionText}>
              {t("Your data is always encrypted")}
            </p>
          </div>
        </div>
      </div>

      {/* Global keyframes */}
      <style>{`
        @keyframes floatUp {
          0%   { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes blob {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50%       { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
      `}</style>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────
const styles = {
  page: {
    display: "flex",
    gap: "24px",
    alignItems: "stretch",
    animation: "floatUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
    fontFamily: "'Geist', 'Inter', system-ui, sans-serif",
    minHeight: "420px",
  },
  // Card
  card: {
    flex: "0 0 52%",
    background: "#ffffff",
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.07)",
    border: "1px solid #f1f5f9",
    display: "flex",
    flexDirection: "column",
    gap: "0px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "20px",
  },
  iconBadge: {
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
  },
  title: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.3px",
  },
  subtitle: {
    fontSize: "13px",
    color: "#94a3b8",
    margin: "2px 0 0",
  },
  divider: {
    height: "1px",
    background: "linear-gradient(to right, #e2e8f0, transparent)",
    marginBottom: "22px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  fieldWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12.5px",
    fontWeight: "600",
    color: "#475569",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  inputRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "13px",
    color: "#94a3b8",
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
    zIndex: 1,
  },
  input: {
    width: "100%",
    padding: "10px 42px 10px 40px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: "#6366f1",
    boxShadow: "0 0 0 3px rgba(99,102,241,0.12)",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    display: "flex",
    alignItems: "center",
    padding: "4px",
    borderRadius: "6px",
    transition: "color 0.15s",
  },
  // Strength
  strengthWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "-4px",
  },
  strengthBars: {
    display: "flex",
    gap: "4px",
    flex: 1,
  },
  strengthBar: {
    flex: 1,
    height: "4px",
    borderRadius: "99px",
  },
  strengthLabel: {
    fontSize: "11.5px",
    fontWeight: "600",
    minWidth: "44px",
    textAlign: "right",
  },
  // Button
  submitBtn: {
    marginTop: "6px",
    padding: "12px 24px",
    background: "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "14.5px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
    transition: "transform 0.18s, box-shadow 0.18s, background 0.3s",
    letterSpacing: "0.01em",
  },
  submitBtnHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(99,102,241,0.45)",
  },
  submitBtnSuccess: {
    background: "linear-gradient(135deg, #22c55e 0%, #4ade80 100%)",
    boxShadow: "0 4px 14px rgba(34,197,94,0.35)",
  },
  btnContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  helpText: {
    fontSize: "12px",
    color: "#94a3b8",
    textAlign: "center",
    margin: 0,
    lineHeight: "1.5",
  },
  // Illustration Panel
  illustrationPanel: {
    flex: 1,
    borderRadius: "20px",
    background: "linear-gradient(145deg, #eef2ff 0%, #f0f9ff 100%)",
    border: "1px solid #e0e7ff",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "340px",
  },
  illustrationInner: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px",
    boxSizing: "border-box",
  },
  blob1: {
    position: "absolute",
    width: "200px",
    height: "200px",
    background: "rgba(99,102,241,0.1)",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    top: "-40px",
    right: "-40px",
    animation: "blob 8s ease-in-out infinite",
  },
  blob2: {
    position: "absolute",
    width: "150px",
    height: "150px",
    background: "rgba(129,140,248,0.08)",
    borderRadius: "30% 60% 70% 40% / 50% 60% 30% 60%",
    bottom: "-30px",
    left: "-20px",
    animation: "blob 10s ease-in-out infinite reverse",
  },
  illustration: {
    width: "100%",
    maxWidth: "260px",
    height: "auto",
    objectFit: "contain",
    position: "relative",
    zIndex: 1,
    filter: "drop-shadow(0 16px 32px rgba(99,102,241,0.15))",
  },
  illustrationCaption: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "16px",
    background: "rgba(255,255,255,0.8)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(99,102,241,0.15)",
    borderRadius: "99px",
    padding: "6px 14px",
    position: "relative",
    zIndex: 1,
  },
  captionDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#22c55e",
    flexShrink: 0,
    boxShadow: "0 0 0 2px rgba(34,197,94,0.25)",
  },
  captionText: {
    fontSize: "12px",
    color: "#475569",
    fontWeight: "500",
    margin: 0,
  },
};

export default ChangePassword;
